import { styled } from "@linaria/react"
import { AnimatePresence, motion } from "framer-motion"
import { observer } from "mobx-react-lite"

import { Avatar } from "@/components/Avatar/Avatar.tsx"
import { DotsProgress } from "@/components/DotsProgress/DotsProgress.tsx"
import { Horizontal } from "@/components/Flex.ts"
import { smallerFontSize } from "@/styles/textStyles.ts"

import type { TypingIndicatorModel } from "@/components/Chat/TypingIndicatorModel.ts"

export const TypingIndicator = observer((props: {
  model: TypingIndicatorModel
}) => {
  const typingUsers = props.model.currentlyTyping.get()

  if (typingUsers.length === 0) {
    return null
  }

  const displayText = typingUsers.length === 1
    ? `${typingUsers[0].name.firstName} is typing`
    : typingUsers.length === 2
      ? `${typingUsers[0].name.firstName} & ${typingUsers[1].name.firstName} are typing`
      : `${typingUsers[0].name.firstName} and ${typingUsers.length - 1} others are typing`

  return (
    <AnimatePresence>
      <Container
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.2 }}
      >
        <AvatarContainer>
          {typingUsers.slice(0, 3).map((user, index) => (
            <AvatarWrapper key={user.id} index={index}>
              <Avatar profile={user} size={20} />
            </AvatarWrapper>
          ))}
        </AvatarContainer>
        <TextContainer>
          <TypingText>{displayText}</TypingText>
          <DotsProgress ref={null} />
        </TextContainer>
      </Container>
    </AnimatePresence>
  )
})

const Container = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 8px;

  max-width: var(--chat-width);
  margin-bottom: 8px;
  padding: 8px 12px;

  border-radius: var(--border-radius-20);
  background: rgb(255 255 255 / 85%);

  box-shadow: 0 2px 8px oklch(0% 0 0deg / 8%);
  backdrop-filter: blur(16px);
`

const AvatarContainer = styled(Horizontal)`
  align-items: center;

  position: relative;
  height: 20px;
`

const AvatarWrapper = styled(Horizontal)<{ index: number }>`
  align-items: center;

  position: relative;
  margin-left: ${props => props.index === 0 ? "0" : "-8px"};
  z-index: ${props => props.index};

  & > * {
    border: 2px solid white;
    border-radius: 50%;
  }
`

const TextContainer = styled(Horizontal)`
  flex: 1;
  align-items: center;
  gap: 8px;
`

const TypingText = styled.span`
  ${smallerFontSize};
`